import React, { useCallback } from 'react';
import { Slider } from '@radix-ui/themes';

export type SliderRowProps = {
  name: string;
  min: number;
  max: number;
  value: number;
  units: string;
  onChange: (v: number) => void;
  /** Step size. Defaults to (max-min)/100, or 1 for integer-looking ranges. */
  step?: number;
  disabled?: boolean;
};

function inferStep(min: number, max: number): number {
  const span = max - min;
  if (span <= 0) return 1;
  if (Number.isInteger(min) && Number.isInteger(max) && span <= 200) return 1;
  return span / 100;
}

function formatValue(value: number, step: number): string {
  if (step >= 1) return value.toFixed(0);
  if (step >= 0.1) return value.toFixed(1);
  if (step >= 0.01) return value.toFixed(2);
  return value.toPrecision(3);
}

/**
 * Named slider row: mono-caps label, Radix `Slider`, mono value + units.
 * Keyboard accessible (Radix primitive). ↑/↓/←/→/PageUp/PageDown all work.
 */
export const SliderRow: React.FC<SliderRowProps> = ({
  name,
  min,
  max,
  value,
  units,
  onChange,
  step,
  disabled,
}) => {
  const effectiveStep = step ?? inferStep(min, max);
  const handleChange = useCallback(
    (next: number[]) => {
      const n = next[0];
      if (typeof n === 'number' && !Number.isNaN(n)) onChange(n);
    },
    [onChange],
  );

  const labelId = `as-slider-${name.replace(/\s+/g, '-')}`;
  return (
    <div className="as-slider-row" role="group" aria-labelledby={labelId}>
      <span id={labelId} className="as-slider-row-label">
        {name}
      </span>
      <div className="as-slider-row-slider">
        <Slider
          min={min}
          max={max}
          step={effectiveStep}
          value={[value]}
          onValueChange={handleChange}
          disabled={disabled}
          aria-label={name}
        />
      </div>
      <span className="as-slider-row-value">
        {formatValue(value, effectiveStep)} {units}
      </span>
    </div>
  );
};
